import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Activity, Radio, Users, Zap, Wifi, WifiOff, Camera, Play, Square, AlertCircle, Eye, ExternalLink, Copy } from 'lucide-react';
import { liveIntelligenceApi } from '../lib/api';
import { WebRTCBroadcaster, getIceConfig } from '../lib/webrtc';
import { toast } from 'sonner';

const LiveMatchConsolePage = () => {
    const { matchId: paramMatchId } = useParams();
    const [matchId, setMatchId] = useState(paramMatchId || 'demo-match-1');
    const [wsStatus, setWsStatus] = useState('disconnected');
    const [session, setSession] = useState(null);
    const [trackingFrame, setTrackingFrame] = useState(null);
    const [score, setScore] = useState({ home: 0, away: 0 });
    const [events, setEvents] = useState([]);
    const [streaming, setStreaming] = useState(false);
    const [webrtcStatus, setWebrtcStatus] = useState('idle');
    const [webrtcViewerCount, setWebrtcViewerCount] = useState(0);
    const [iceInfo, setIceInfo] = useState(null);
    const wsRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamIntervalRef = useRef(null);
    const broadcasterRef = useRef(null);
    const mediaStreamRef = useRef(null);

    // ========== WEBSOCKET ==========
    const connect = () => {
        const backend = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        const ws = new WebSocket(`${backend}/api/live-intelligence/ws/${matchId}`);
        wsRef.current = ws;

        ws.onopen = () => {
            setWsStatus('connected');
            ws.send(JSON.stringify({ action: 'subscribe' }));
            toast.success('Connected to live match feed');
        };
        ws.onmessage = (msg) => {
            const data = JSON.parse(msg.data);
            if (data.type === 'session_state') {
                setSession(data.data);
                if (data.data?.current_score) setScore(data.data.current_score);
                if (data.data?.events) setEvents(data.data.events);
            } else if (data.type === 'score_update') {
                setScore({ home: data.data.home, away: data.data.away });
            } else if (data.type === 'event') {
                setEvents((prev) => [data.data, ...prev].slice(0, 20));
            } else if (data.type === 'tracking_update') {
                setTrackingFrame(data.data);
            }
        };
        ws.onclose = () => setWsStatus('disconnected');
        ws.onerror = () => setWsStatus('error');
    };

    const disconnect = () => {
        wsRef.current?.close();
        wsRef.current = null;
        setWsStatus('disconnected');
    };

    useEffect(() => {
        connect();
        loadSession();
        return () => {
            disconnect();
            stopStream();
        };
        // eslint-disable-next-line
    }, [matchId]);

    const loadSession = async () => {
        try {
            const res = await liveIntelligenceApi.getSession(matchId);
            if (res.data?.exists) {
                setSession(res.data);
                if (res.data.current_score) setScore(res.data.current_score);
                if (res.data.events) setEvents(res.data.events);
            }
        } catch (e) {}
    };

    // ========== SCORE PUSH ==========
    const pushScoreUpdate = async (homeDelta, awayDelta) => {
        const newScore = { home: Math.max(0, score.home + homeDelta), away: Math.max(0, score.away + awayDelta) };
        const fd = new FormData();
        fd.append('home', newScore.home);
        fd.append('away', newScore.away);
        fd.append('period', 'live');
        try {
            await liveIntelligenceApi.pushScore(matchId, fd);
            // WS will broadcast back the score
        } catch (e) {
            toast.error('Failed to push score');
        }
    };

    const pushEvent = async (type, team, description) => {
        const fd = new FormData();
        fd.append('event_type', type);
        fd.append('team', team);
        fd.append('description', description);
        try {
            await liveIntelligenceApi.pushEvent(matchId, fd);
        } catch (e) {
            toast.error('Failed to push event');
        }
    };

    // Load ICE config once
    useEffect(() => {
        getIceConfig().then(setIceInfo);
    }, []);

    // ========== STREAM CAPTURE (WebRTC + optional ML frame analysis) ==========
    const startStream = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 1280, height: 720 },
                audio: true
            });
            mediaStreamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.muted = true;
                await videoRef.current.play();
            }
            setStreaming(true);

            // Start WebRTC broadcaster
            const broadcaster = new WebRTCBroadcaster(matchId, stream, {
                onStatus: setWebrtcStatus,
                onViewerCount: setWebrtcViewerCount,
            });
            broadcasterRef.current = broadcaster;
            await broadcaster.connect();

            // Also send 1 frame every 1.5s for ML analysis
            streamIntervalRef.current = setInterval(captureAndSendFrame, 1500);
            toast.success('Live broadcast started — share the watch link');
        } catch (e) {
            toast.error('Camera/mic access denied: ' + e.message);
        }
    };

    const stopStream = () => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            mediaStreamRef.current = null;
        }
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject = null;
        }
        if (broadcasterRef.current) {
            broadcasterRef.current.disconnect();
            broadcasterRef.current = null;
        }
        if (streamIntervalRef.current) {
            clearInterval(streamIntervalRef.current);
            streamIntervalRef.current = null;
        }
        setStreaming(false);
        setWebrtcStatus('idle');
        setWebrtcViewerCount(0);
    };

    const watchUrl = `${window.location.origin}/live-watch/${matchId}`;
    const copyWatchLink = async () => {
        try {
            await navigator.clipboard.writeText(watchUrl);
            toast.success('Watch link copied');
        } catch {
            toast.error('Could not copy');
        }
    };

    const captureAndSendFrame = async () => {
        if (!videoRef.current || !canvasRef.current) return;
        const canvas = canvasRef.current;
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoRef.current, 0, 0);
        const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.7));
        if (!blob) return;

        const fd = new FormData();
        fd.append('frame', blob, 'frame.jpg');
        try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live-intelligence/match/${matchId}/frame-snapshot`, {
                method: 'POST',
                body: fd
            });
        } catch (e) {
            console.warn('Frame upload failed', e);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${wsStatus === 'connected' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            <Radio className={`w-5 h-5 ${wsStatus === 'connected' ? 'text-green-400 animate-pulse' : 'text-red-400'}`} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Live Match Console</h1>
                            <p className="text-sm text-muted-foreground">
                                {wsStatus === 'connected' ? <span className="text-green-400">● Connected</span> : <span className="text-red-400">● Disconnected</span>}
                                {' '}— Match: <code className="text-xs bg-white/10 px-1.5 py-0.5 rounded" data-testid="live-match-id">{matchId}</code>
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {wsStatus === 'connected'
                            ? <Button onClick={disconnect} variant="outline" className="border-red-500/40 text-red-400" data-testid="ws-disconnect-btn"><WifiOff className="w-4 h-4 mr-2" />Disconnect</Button>
                            : <Button onClick={connect} className="bg-green-500 hover:bg-green-600" data-testid="ws-connect-btn"><Wifi className="w-4 h-4 mr-2" />Connect</Button>
                        }
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Live Video + Tracking */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card className="bg-[#0d2847] border-white/10 p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold flex items-center gap-2"><Camera className="w-4 h-4 text-cyan-400" /> Live Camera Feed</h3>
                                {streaming
                                    ? <Button onClick={stopStream} size="sm" variant="outline" className="border-red-500/40 text-red-400" data-testid="stream-stop-btn"><Square className="w-4 h-4 mr-1" />Stop</Button>
                                    : <Button onClick={startStream} size="sm" className="bg-cyan-500 hover:bg-cyan-600" data-testid="stream-start-btn"><Play className="w-4 h-4 mr-1" />Start Live Tracking</Button>
                                }
                            </div>
                            <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-white/10">
                                <video ref={videoRef} className="w-full h-full object-cover" muted />
                                <canvas ref={canvasRef} className="hidden" />
                                {!streaming && (
                                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                                        <div className="text-center">
                                            <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                            <p className="text-sm">Click Start Live Tracking to enable real-time YOLOv8 detection</p>
                                        </div>
                                    </div>
                                )}
                                {/* Bounding box overlay */}
                                {streaming && trackingFrame?.detections?.map((d, i) => (
                                    <div key={i} className="absolute border-2 border-cyan-400 rounded" style={{
                                        left: `${d.bbox_norm[0] * 100}%`,
                                        top: `${d.bbox_norm[1] * 100}%`,
                                        width: `${(d.bbox_norm[2] - d.bbox_norm[0]) * 100}%`,
                                        height: `${(d.bbox_norm[3] - d.bbox_norm[1]) * 100}%`,
                                    }}>
                                        <span className="absolute -top-5 left-0 text-xs bg-cyan-500 text-black px-1 rounded">P{d.id} {(d.confidence * 100).toFixed(0)}%</span>
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-4 gap-2 mt-3 text-center text-xs">
                                <div className="bg-[#0a1628] p-2 rounded"><div className="text-cyan-400 font-bold text-lg" data-testid="stat-frames">{trackingFrame?.frames_processed ?? session?.frames_processed ?? 0}</div><div className="text-muted-foreground">Frames Analyzed</div></div>
                                <div className="bg-[#0a1628] p-2 rounded"><div className="text-purple-400 font-bold text-lg">{trackingFrame?.detections_count ?? 0}</div><div className="text-muted-foreground">Players Detected</div></div>
                                <div className="bg-[#0a1628] p-2 rounded"><div className="text-green-400 font-bold text-lg" data-testid="stat-webrtc-viewers">{webrtcViewerCount}</div><div className="text-muted-foreground">WebRTC Viewers</div></div>
                                <div className="bg-[#0a1628] p-2 rounded"><div className="text-yellow-400 font-bold text-lg">{session?.active_subscribers ?? 0}</div><div className="text-muted-foreground">Stats Viewers</div></div>
                            </div>
                        </Card>

                        {/* Player Analytics */}
                        <Card className="bg-[#0d2847] border-white/10 p-5">
                            <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-purple-400" /> Player Analytics</h3>
                            {(session?.players?.length || 0) === 0 ? (
                                <p className="text-sm text-muted-foreground">No tracking data yet — start live tracking to populate.</p>
                            ) : (
                                <div className="space-y-2">
                                    {session.players.slice(0, 8).map((p) => (
                                        <div key={p.player_id} className="flex items-center justify-between p-2 bg-[#0a1628] rounded text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 font-bold">{p.player_id}</div>
                                                <span>Player {p.player_id}</span>
                                            </div>
                                            <div className="flex gap-4 text-xs">
                                                <span><span className="text-muted-foreground">Frames:</span> {p.frames_seen}</span>
                                                <span><span className="text-muted-foreground">Distance:</span> ~{p.estimated_distance_m}m</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>

                    {/* Right: Score + Events */}
                    <div className="space-y-4">
                        {/* Scoreboard */}
                        <Card className="bg-[#0d2847] border-white/10 p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> Live Score</h3>
                                <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full animate-pulse">● LIVE</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="text-center p-4 bg-[#0a1628] rounded border border-white/10">
                                    <div className="text-xs text-muted-foreground mb-1">HOME</div>
                                    <div className="text-5xl font-bold text-cyan-400" data-testid="live-home-score">{score.home}</div>
                                    <div className="flex gap-1 justify-center mt-3">
                                        <Button size="sm" variant="outline" className="border-white/10 h-7 px-2" onClick={() => pushScoreUpdate(1, 0)} data-testid="home-plus-1">+1</Button>
                                        <Button size="sm" variant="outline" className="border-white/10 h-7 px-2" onClick={() => pushScoreUpdate(-1, 0)}>−</Button>
                                    </div>
                                </div>
                                <div className="text-center p-4 bg-[#0a1628] rounded border border-white/10">
                                    <div className="text-xs text-muted-foreground mb-1">AWAY</div>
                                    <div className="text-5xl font-bold text-purple-400" data-testid="live-away-score">{score.away}</div>
                                    <div className="flex gap-1 justify-center mt-3">
                                        <Button size="sm" variant="outline" className="border-white/10 h-7 px-2" onClick={() => pushScoreUpdate(0, 1)} data-testid="away-plus-1">+1</Button>
                                        <Button size="sm" variant="outline" className="border-white/10 h-7 px-2" onClick={() => pushScoreUpdate(0, -1)}>−</Button>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => pushEvent('goal', 'home', 'Goal scored')} data-testid="event-goal-home">Goal Home</Button>
                                <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => pushEvent('goal', 'away', 'Goal scored')} data-testid="event-goal-away">Goal Away</Button>
                                <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => pushEvent('foul', 'home', 'Foul committed')}>Foul Home</Button>
                                <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => pushEvent('foul', 'away', 'Foul committed')}>Foul Away</Button>
                            </div>
                        </Card>

                        {/* Events Feed */}
                        <Card className="bg-[#0d2847] border-white/10 p-5">
                            <h3 className="font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-green-400" /> Live Events</h3>
                            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="live-events-feed">
                                {events.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-4 text-center">No events yet</p>
                                ) : events.map((e) => (
                                    <div key={e.id} className="p-2 bg-[#0a1628] rounded text-xs border-l-2 border-cyan-400">
                                        <div className="flex items-center gap-2 font-medium">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${e.team === 'home' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'}`}>{e.team?.toUpperCase()}</span>
                                            <span className="capitalize">{e.type}</span>
                                        </div>
                                        <div className="text-muted-foreground mt-0.5">{e.description}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(e.timestamp).toLocaleTimeString()}</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Tech Disclaimer */}
                <Card className="bg-cyan-500/5 border-cyan-500/30 p-4 mt-6">
                    <div className="flex items-start gap-2 flex-wrap">
                        <AlertCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-muted-foreground flex-1 min-w-0">
                            <strong className="text-cyan-400">WebRTC P2P Broadcast Active.</strong> Audio + video stream peer-to-peer to all viewers via{' '}
                            <span className="text-cyan-400">{iceInfo?.has_turn ? 'STUN + TURN' : 'STUN only'}</span>.
                            {iceInfo?.stun_only && (
                                <span className="text-yellow-400"> ⚠ For viewers behind strict NAT, configure TURN_URLS / TURN_USERNAME / TURN_PASSWORD in backend/.env.</span>
                            )}
                            <span className="block mt-1">
                                <strong className="text-white">Watch URL:</strong>{' '}
                                <a href={watchUrl} target="_blank" rel="noreferrer" className="text-cyan-400 underline" data-testid="watch-url-link">{watchUrl}</a>
                                <Button onClick={copyWatchLink} variant="ghost" size="sm" className="h-6 ml-2" data-testid="copy-watch-link-btn">
                                    <Copy className="w-3 h-3 mr-1" /> Copy
                                </Button>
                                <Link to={`/live-watch/${matchId}`} target="_blank" className="ml-1">
                                    <Button variant="ghost" size="sm" className="h-6"><ExternalLink className="w-3 h-3 mr-1" />Open</Button>
                                </Link>
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default LiveMatchConsolePage;
